<template>
	<!--
	  Device Login Screen for Tizen TVs
	  - Shows a short user code and a QR code that points to the verification URL.
	  - While displayed, the app polls the backend for an access token, completing the OAuth Device Authorization Grant.
	-->
	<div class="device-login">
		<h2>Scan QR to authorize TV</h2>
		<p>
			<!-- Human-readable short code the user can enter on another device -->
			Code: <strong>{{ userCode }}</strong>
		</p>
		<!-- Canvas used to render the QR code that encodes the verification URL -->
		<canvas ref="qrCanvas"></canvas>
		<!-- Any error surfaced during device flow or polling -->
		<p v-if="error">{{ error }}</p>
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted } from "vue"
	import { useCurrentUser } from "../composables/useCurrentUser"
	import axios from "axios"
	import QRCode from "qrcode"
	import { url } from "../helper"
	import { useRouter } from "vue-router"
	import { useI18n } from "vue-i18n"
	import qs from "qs"

	// Get selected locale (if required by backend or user fetch)
	const { locale } = useI18n()

	// Refs bound to template/state
	const qrCanvas = ref<HTMLCanvasElement | null>(null) // canvas element for QR rendering
	const userCode = ref("") // short code the user sees
	const deviceCode = ref("") // device_code returned by the device authorization endpoint
	const pollInterval = ref(5) // polling interval in seconds (server may override)
	const error = ref("") // error message to display to the user

	// Router instance for navigation after successful auth
	const router = useRouter()
	// Fetcher that populates the current user store/profile once we have a token
	const { fetchCurrentUser } = useCurrentUser(locale)

	// Starts the OAuth 2.0 Device Authorization flow
	async function startDeviceFlow() {
		try {
			// Begin device authorization: server returns device_code, user_code, interval, and verification URIs
			const res = await axios.post(
				`${url}/oauth/device_authorization`,
				qs.stringify({
					// Public client identifier configured on the authorization server
					client_id: "zQTiAtJD02-HEn6fvUTE_I2Y5iKFkA9qJN4vkVDKNbI",
					// Requesting basic OpenID Connect scopes
					scope: "openid profile email",
				}),
				{ headers: { "Content-Type": "application/x-www-form-urlencoded" } }
			)

			// Persist values we need for display and subsequent token polling
			deviceCode.value = res.data.device_code
			userCode.value = res.data.user_code
			pollInterval.value = res.data.interval || 5

			// Draw the QR code into the canvas so users can quickly open the verification URL on a phone
			if (qrCanvas.value) {
				QRCode.toCanvas(qrCanvas.value, res.data.verification_uri_complete, { width: 300 })
			}

			// Start polling the token endpoint until the user authorizes or the code expires
			pollForToken()
		} catch (e: any) {
			// Surface the error to the UI; you may want to localize or provide friendlier copy
			error.value = e.message
		}
	}

	// Polls the token endpoint at the server-specified interval until authorization completes
	async function pollForToken() {
		// Keep a reference to clear later when we are done or when the device code expires
		const timer = setInterval(async () => {
			try {
				// Device Token request per OAuth 2.0 Device Code spec
				const payload = qs.stringify({
					grant_type: "urn:ietf:params:oauth:grant-type:device_code",
					device_code: deviceCode.value,
					client_id: "zQTiAtJD02-HEn6fvUTE_I2Y5iKFkA9qJN4vkVDKNbI",
				})
				const res = await axios.post(`${url}/oauth/device_token`, payload, {
					headers: { "Content-Type": "application/x-www-form-urlencoded" },
				})

				// On success, store tokens and move to the post-login screen
				if (res.data.access_token) {
					clearInterval(timer)
					// Persist tokens for authenticated requests and renewal
					localStorage.setItem("jwt", res.data.access_token)
					localStorage.setItem("refresh_token", res.data.refresh_token)
					localStorage.setItem("token_expiry", String(Date.now() + res.data.expires_in * 1000))
					const redirectPath = "/profile"

					// Load the current user context and navigate to the profile page
					await fetchCurrentUser()
					sessionStorage.removeItem("post_auth_redirect") // cleanup any redirect hints
					router.push(redirectPath)
				}
			} catch (err: any) {
				// authorization_pending: user hasn't confirmed yet; keep polling
				if (err.response?.data?.error === "authorization_pending") return
				// expired_token: device_code is no longer valid; stop polling and inform the user
				if (err.response?.data?.error === "expired_token") {
					clearInterval(timer)
					error.value = "Login expired. Please try again."
				}
			}
		}, pollInterval.value * 1000)
	}

	// Kick off the device flow when the component mounts
	onMounted(() => startDeviceFlow())
</script>

<style scoped>
	/* Center the device login UI and give breathing room to the QR canvas */
	.device-login {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100vh;
		text-align: center;
	}
	.device-login canvas {
		margin-top: 20px;
	}
</style>
